import type { RatioCard, RatioUnit } from '../../../../data/ServerApiClient'
import { formatRatioValue } from '../cfoFormat'
import { ratioBenchmarkAnchor, ratioBenchmarkSource } from '../cfoRatioBenchmarking'
import { buildRatioComparisonRows } from '../cfoRatioComparisonRows'

// Shared render model for `ratio_card` report blocks — the "pin any ratio card
// to the board pack" feature. The studio preview (React), the HTML packet/deck
// export, and the PPTX export all build their ratio-card sections from this
// one model so the three surfaces never disagree on values or labels.
//
// Data resolution order:
//   1. `response.ratio_cards[source.ratio_id]` — the live served card the
//      server embeds for every pinned block (and freezes into publish
//      snapshots), so pinned cards keep refreshing as months roll forward.
//   2. `block.content.card` — the compact copy captured at pin time. This is
//      the only data source for client-synthesized cards (e.g. the equity
//      multiplier card the Ratios page derives locally) and the fallback when
//      the ratio has left the library.

export type RatioCardBlockStat = { label: string; value: string }

export type RatioCardBlockModel = {
  card: RatioCard
  /** True when rendering the frozen pin-time copy instead of live data. */
  frozen: boolean
  assessment: 'healthy' | 'watch' | 'concern' | 'neutral'
  assessmentLabel: string
  directionLabel: string
  explanation: string
  formula: string
  stats: RatioCardBlockStat[]
  /** Chronological finite trend tail (up to 12 points). */
  trend: Array<{ period: string; value: number }>
  benchmarkValue: number | null
  benchmarkLabel: string
  comparisonRows: string[][]
  note: string
  unit: RatioUnit
}

const cleanText = (value: unknown): string => String(value ?? '').trim()

const looksLikeRatioCard = (value: any): value is RatioCard => (
  Boolean(value)
  && typeof value === 'object'
  && typeof value.id === 'string'
  && typeof value.label === 'string'
  && typeof value.unit === 'string'
)

/** Fields worth freezing at pin time — everything the block model reads. */
export const pinnedRatioCardSnapshot = (ratio: RatioCard): Record<string, any> => ({
  id: ratio.id,
  label: ratio.label,
  category: ratio.category,
  unit: ratio.unit,
  description: ratio.description,
  formula: ratio.formula,
  higher_is_better: ratio.higher_is_better ?? null,
  benchmark: ratio.benchmark,
  latest: ratio.latest,
  latest_period: ratio.latest_period,
  trend: (ratio.trend || []).slice(-24),
  trend_direction: ratio.trend_direction,
  assessment: ratio.assessment,
  comparisons: ratio.comparisons || [],
  components: (ratio.components || []).slice(0, 8),
  interpretation: ratio.interpretation,
  ai_interpretation: ratio.ai_interpretation ?? null,
})

/**
 * Resolve the card a `ratio_card` block renders. Prefers the live served card
 * embedded on the presentation response; falls back to the frozen pin-time
 * copy. Returns null (block renders an unavailable note) when neither exists.
 */
export const resolveRatioCardForBlock = (
  ratioCards: unknown,
  block: { source?: Record<string, any>; content?: Record<string, any> },
): { card: RatioCard; frozen: boolean } | null => {
  const ratioId = cleanText(block.source?.ratio_id || block.source?.ratioId)
  if (ratioId && ratioCards && typeof ratioCards === 'object') {
    const live = (ratioCards as Record<string, unknown>)[ratioId]
    if (looksLikeRatioCard(live)) return { card: live, frozen: false }
  }
  const frozenCard = block.content?.card
  if (looksLikeRatioCard(frozenCard)) return { card: frozenCard, frozen: true }
  return null
}

const averageFinite = (values: Array<number | null | undefined>): number | null => {
  const finite = values.filter((value): value is number => value != null && Number.isFinite(value))
  if (!finite.length) return null
  return finite.reduce((sum, value) => sum + value, 0) / finite.length
}

/** The shared anchor from cfoRatioBenchmarking.ts (non-React module, safe for the export bundle). */
const benchmarkAnchor = ratioBenchmarkAnchor

const benchmarkSourceLabel = (ratio: RatioCard): string => {
  const source = ratioBenchmarkSource(ratio)
  if (source?.kind === 'history') return `Own ${source.tag}`
  if (source?.kind === 'band') return 'Healthy benchmark'
  return 'IRS average'
}

const ASSESSMENT_LABELS: Record<string, string> = {
  healthy: 'Healthy',
  watch: 'Keep an eye',
  concern: 'Needs attention',
  neutral: 'Informational',
}

const DIRECTION_LABELS: Record<string, string> = {
  improving: '12-mo improving',
  declining: '12-mo declining',
  stable: '12-mo stable',
}

const TRAILING_WINDOWS: Array<{ id: string; label: string; periods: number }> = [
  { id: 'trailing_12', label: 'Trailing 12 mo', periods: 12 },
  { id: 'trailing_6', label: 'Trailing 6 mo', periods: 6 },
  { id: 'trailing_3', label: 'Trailing 3 mo', periods: 3 },
]

/** Longest fully-covered trailing average, so the label never overstates the window. */
const trailingContext = (ratio: RatioCard): { label: string; value: number } | null => {
  const values = (ratio.trend || []).map((point) => point.value)
  for (const window of TRAILING_WINDOWS) {
    const finite = values.slice(-window.periods).filter((value): value is number => value != null && Number.isFinite(value))
    if (finite.length >= window.periods) {
      return { label: window.label, value: finite.reduce((sum, value) => sum + value, 0) / finite.length }
    }
  }
  return null
}

export const buildRatioCardBlockModel = (
  ratioCards: unknown,
  block: { source?: Record<string, any>; content?: Record<string, any> },
): RatioCardBlockModel | null => {
  const resolved = resolveRatioCardForBlock(ratioCards, block)
  if (!resolved) return null
  const { card, frozen } = resolved

  const window = cleanText(block.source?.window)
  const windowOption = TRAILING_WINDOWS.find((option) => option.id === window)
  const trailing = windowOption
    ? (() => {
      const value = averageFinite((card.trend || []).map((point) => point.value).slice(-windowOption.periods))
      return value == null ? null : { label: windowOption.label, value }
    })()
    : trailingContext(card)

  const bench = benchmarkAnchor(card)
  const benchLabel = benchmarkSourceLabel(card)
  const assessment = (['healthy', 'watch', 'concern', 'neutral'] as const).includes(card.assessment as any)
    ? card.assessment as RatioCardBlockModel['assessment']
    : 'neutral'

  const stats: RatioCardBlockStat[] = [
    { label: 'Latest', value: formatRatioValue(card.latest, card.unit) },
    { label: trailing?.label || 'Trailing 12 mo', value: formatRatioValue(trailing?.value ?? null, card.unit) },
    { label: benchLabel, value: formatRatioValue(bench, card.unit) },
  ]

  const trend = (card.trend || [])
    .filter((point) => point && Number.isFinite(point.value))
    .slice(-12)

  return {
    card,
    frozen,
    assessment,
    assessmentLabel: ASSESSMENT_LABELS[assessment] || ASSESSMENT_LABELS.neutral,
    directionLabel: DIRECTION_LABELS[cleanText(card.trend_direction)] || '',
    explanation: cleanText(card.ai_interpretation) || cleanText(card.interpretation),
    formula: cleanText(card.formula),
    stats,
    trend,
    benchmarkValue: bench,
    benchmarkLabel: benchLabel,
    comparisonRows: buildRatioComparisonRows(card),
    note: cleanText(card.benchmark?.note) + (frozen ? `${cleanText(card.benchmark?.note) ? ' ' : ''}Values captured when this card was added to the report.` : ''),
    unit: card.unit,
  }
}

export type RatioCardSparklineGeometry = {
  width: number
  height: number
  linePoints: string
  areaPath: string
  benchmarkY: number | null
  points: Array<{ x: number; y: number; period: string; value: number }>
  firstLabel: { x: number; y: number; text: string }
  lastLabel: { x: number; y: number; text: string }
}

const shortMonth = (period: string): string => {
  const parsed = new Date(`${String(period).slice(0, 10)}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return String(period).slice(0, 7)
  return parsed.toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' })
}

export const ratioCardTickLabel = shortMonth

/**
 * Pure sparkline geometry (trend line + optional dashed benchmark) shared by
 * the studio preview SVG and the HTML export SVG, so both charts plot the
 * exact same shape. Returns null when there are fewer than 2 points.
 */
export const buildRatioCardSparkline = (
  model: Pick<RatioCardBlockModel, 'trend' | 'benchmarkValue' | 'unit'>,
  width = 560,
  height = 170,
): RatioCardSparklineGeometry | null => {
  const points = model.trend
  if (points.length < 2) return null
  const values = points.map((point) => point.value)
  const domain = model.benchmarkValue == null ? values : [...values, model.benchmarkValue]
  const min = Math.min(...domain)
  const max = Math.max(...domain)
  const pad = (max - min || Math.abs(max) || 1) * 0.16
  const yMin = min - pad
  const yMax = max + pad
  const left = 10
  const right = 10
  const top = 26
  const bottom = 22
  const plotW = width - left - right
  const plotH = height - top - bottom
  const toX = (idx: number) => left + (idx / Math.max(points.length - 1, 1)) * plotW
  const toY = (value: number) => top + ((yMax - value) / (yMax - yMin || 1)) * plotH
  const mapped = points.map((point, idx) => ({ x: toX(idx), y: toY(point.value), period: point.period, value: point.value }))
  const linePoints = mapped.map((point) => `${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(' ')
  const areaPath = `M ${mapped[0].x.toFixed(1)},${mapped[0].y.toFixed(1)} `
    + mapped.slice(1).map((point) => `L ${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(' ')
    + ` L ${mapped[mapped.length - 1].x.toFixed(1)},${(top + plotH).toFixed(1)} L ${mapped[0].x.toFixed(1)},${(top + plotH).toFixed(1)} Z`
  const clampY = (y: number) => Math.min(top + plotH - 4, Math.max(12, y))
  const first = mapped[0]
  const last = mapped[mapped.length - 1]
  return {
    width,
    height,
    linePoints,
    areaPath,
    benchmarkY: model.benchmarkValue == null ? null : toY(model.benchmarkValue),
    points: mapped,
    firstLabel: {
      x: first.x + 4,
      y: clampY(first.y - 10),
      text: `${shortMonth(first.period)} ${formatRatioValue(first.value, model.unit)}`,
    },
    lastLabel: {
      x: last.x - 4,
      y: clampY(last.y - 10),
      text: `${shortMonth(last.period)} ${formatRatioValue(last.value, model.unit)}`,
    },
  }
}

export const formatRatioCardValue = formatRatioValue
